import React from 'react';
import {
  IconTwitter,
  IconInstagram,
  VisuallyHidden,
  TypeList,
  TypeListItem,
} from '@spotify-internal/encore-web';
import { ResponsiveLogo, Link } from '../../ui';
import androidIcon from '../images/footer-android.svg';
import iosIcon from '../images/footer-ios.svg';
import {
  FooterWrapper,
  FooterSection,
  SectionHeader,
  InlineList,
  CircleIcon,
  Copyright,
} from './Footer.styles';

const TRACK_CAT = 'Footer';
const YEAR = new Date().getFullYear();

export const Footer: React.FC = () => {
  return (
    <FooterWrapper>
      <FooterSection>
        <Link
          href="/?ref=footer"
          track={[TRACK_CAT, 'Logo']}
          highlight
          data-testid="footer-logo"
        >
          <ResponsiveLogo />
        </Link>
      </FooterSection>

      <FooterSection>
        <SectionHeader>Fine Print</SectionHeader>
        <TypeList.ul>
          <TypeListItem>
            <Link
              data-testid="footer-legal"
              href="https://www.spotify.com/us/legal/spotify-for-artists-terms-and-conditions/"
              external
              track={[TRACK_CAT, 'Legal']}
            >
              Legal
            </Link>
          </TypeListItem>
          <TypeListItem>
            <Link
              data-testid="footer-privacy"
              href="https://www.spotify.com/legal/privacy-policy"
              external
              track={[TRACK_CAT, 'Privacy Policy']}
            >
              Privacy Policy
            </Link>
          </TypeListItem>
          <TypeListItem>
            <Link
              data-testid="footer-cookies"
              href="https://www.spotify.com/legal/privacy-policy/#s13"
              external
              track={[TRACK_CAT, 'Cookies']}
            >
              Cookies
            </Link>
          </TypeListItem>
        </TypeList.ul>
      </FooterSection>

      <FooterSection>
        <SectionHeader>Need help?</SectionHeader>
        <TypeList.ul>
          <TypeListItem>
            <Link
              href="/contact"
              track={[TRACK_CAT, 'Contact Us']}
              data-testid="footer-contact"
            >
              Contact Us
            </Link>
          </TypeListItem>
          <TypeListItem>
            <Link
              data-testid="footer-claim"
              href="/claim"
              track={[TRACK_CAT, 'Get Access']}
            >
              Get Access
            </Link>
          </TypeListItem>
        </TypeList.ul>
      </FooterSection>

      <FooterSection>
        <SectionHeader>Creator tools</SectionHeader>
        <TypeList.ul>
          <TypeListItem>
            <Link
              data-testid="footer-anchor"
              external
              href="https://app.adjust.com/iya18ah?redirect=https%3A%2F%2Fanchor.fm%2Fapp"
              track={[TRACK_CAT, 'Creator Tools - Anchor']}
            >
              Anchor
            </Link>
          </TypeListItem>
          <TypeListItem>
            <Link
              data-testid="footer-soundbetter"
              external
              href="https://soundbetter.com/"
              track={[TRACK_CAT, 'Creator Tools - SoundBetter']}
            >
              SoundBetter
            </Link>
          </TypeListItem>
          <TypeListItem>
            <Link
              data-testid="footer-soundtrap"
              external
              href="https://www.soundtrap.com/?utm_source=spotify_site&utm_medium=footer_menulink&utm_campaign=alwayson_global_soundtrap_destination"
              track={[TRACK_CAT, 'Creator Tools - Soundtrap']}
            >
              Soundtrap
            </Link>
          </TypeListItem>
          <TypeListItem>
            <Link
              data-testid="footer-s4p"
              external
              href="https://podcasters.spotify.com/?utm_source=artists.spotify.com"
              track={[TRACK_CAT, 'Creator Tools - Spotify for Podcasters']}
            >
              Spotify for Podcasters
            </Link>
          </TypeListItem>
          <TypeListItem>
            <Link
              data-testid="footer-spa"
              external
              href="https://publishers.spotify.com/?utm_source=artists.spotify.com"
              track={[
                TRACK_CAT,
                'Creator Tools - Spotify Publishing Analytics',
              ]}
            >
              Spotify Publishing
              <br />
              Analytics
            </Link>
          </TypeListItem>
        </TypeList.ul>
      </FooterSection>

      <FooterSection>
        <SectionHeader>Follow us</SectionHeader>
        <InlineList>
          <TypeListItem>
            <Link
              data-testid="footer-twitter"
              highlight
              external
              href="https://twitter.com/spotifyartists"
              track={[TRACK_CAT, 'Social - Twitter']}
            >
              <CircleIcon as={IconTwitter} aria-label="Twitter" />
              <VisuallyHidden>Twitter</VisuallyHidden>
            </Link>
          </TypeListItem>
          <TypeListItem>
            <Link
              data-testid="footer-instagram"
              highlight
              external
              href="https://www.instagram.com/spotifyforartists/"
              track={[TRACK_CAT, 'Social - Instagram']}
            >
              <CircleIcon as={IconInstagram} aria-label="Instagram" />
              <VisuallyHidden>Instagram</VisuallyHidden>
            </Link>
          </TypeListItem>
        </InlineList>
      </FooterSection>

      <FooterSection>
        <SectionHeader>Get the app</SectionHeader>
        <TypeList.ul>
          <TypeListItem>
            <Link
              data-testid="footer-ios"
              highlight
              external
              href="https://itunes.apple.com/us/app/spotify-for-artists/id1222021797"
              track={[TRACK_CAT, 'iOS App']}
            >
              <img
                data-src={iosIcon}
                alt="Download banner for iOS"
                className="lazyload"
              />
            </Link>
          </TypeListItem>
          <TypeListItem>
            <Link
              data-testid="footer-android"
              highlight
              external
              href="https://play.google.com/store/apps/details?id=com.spotify.s4a"
              track={[TRACK_CAT, 'Android App']}
            >
              <img
                data-src={androidIcon}
                alt="Download banner for Android"
                className="lazyload"
              />
            </Link>
          </TypeListItem>
        </TypeList.ul>
      </FooterSection>

      <FooterSection>
        <Copyright data-testid="footer-copyright">
          &copy; {YEAR} Spotify AB
        </Copyright>
      </FooterSection>
    </FooterWrapper>
  );
};
